<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import type { BreadCrumb } from '@/router'
import {
  Breadcrumb as BreadcrumbSDL,
  type BreadcrumbItemData,
} from '@easybell/caramel-vue3'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useDevicesStore } from '@/stores/devices/store'
import { usePromptsStore } from '@/stores/prompts/store'
import { useOfficeDesksStore } from '@/stores/officeDesks/store'
import { useCtaStore } from '@/stores/cta/store'
import { useFeaturesStore } from '@/stores/features/store'
import { getEKPUrl } from '@/utils/environment'
import LinkTag from '@/components/LinkTag.vue'

const ctaStore = useCtaStore()

const i18n = useI18n()

const route = useRoute()

const extensionsStore = useExtensionsStore()
const devicesStore = useDevicesStore()
const promptsStore = usePromptsStore()
const officeDesksStore = useOfficeDesksStore()

const ctaEditionLabel = computed(() => {
  const edition = ctaStore.ctaData?.edition

  if (!edition) {
    return ''
  }

  const label = edition === 'standard' ? 'classic' : edition

  return label.charAt(0).toUpperCase() + label.slice(1)
})

const breadcrumbStoreMap: Record<
  string,
  {
    paramKey: string
    store: { list: any[] }
  }
> = {
  'uca-users-edit': { paramKey: 'userId', store: extensionsStore },
  'uca-resources-edit': { paramKey: 'resourceId', store: extensionsStore },
  'uca-device-edit': { paramKey: 'deviceId', store: devicesStore },
  'uca-audiofiles-edit': { paramKey: 'audioId', store: promptsStore },
  'uca-office-desk-edit': { paramKey: 'officeDeskId', store: officeDesksStore },
  'uca-group-edit': { paramKey: 'groupId', store: extensionsStore },
}

const getBreadcrumbLabel = computed(() => (breadcrumb: BreadCrumb) => {
  const storeMapping = breadcrumbStoreMap[breadcrumb.name]

  if (storeMapping) {
    const paramValue = route.params[storeMapping.paramKey]

    if (paramValue) {
      const id = Number(paramValue)
      const item = storeMapping.store.list.find((item) => item.id === id)
      return item?.name || paramValue
    }
  }

  return i18n.t('breadcrumb.' + breadcrumb.name, {
    ...route.params,
    ...route.query,
  })
})

const breadcrumbs = computed((): BreadcrumbItemData[] => {
  return [
    // Home link
    {
      iconLabel: 'Home',
      url: useFeaturesStore().isUcaMode ? getEKPUrl() : '/',
      dataTest: 'breadcrumb-home-link',
    },
    // CTA edition
    ...(useFeaturesStore().isUcaMode
      ? [
          {
            label: i18n.t('navigation.cloud-telefonanlage.label'),
            subText: ctaEditionLabel.value,
          },
        ]
      : []),
    // Route breadcrumbs
    ...(route.meta.breadcrumb ?? []).map((entry) => ({
      label: getBreadcrumbLabel.value(entry),
      url: { name: entry.name },
      dataTest: 'breadcrumb-link',
    })),
  ]
})
</script>

<template>
  <BreadcrumbSDL
    :linkTag="LinkTag"
    :breadcrumbs="breadcrumbs"
    data-test="breadcrumbs"
    class="max-w-full [overflow-x:clip]"
  />
</template>
